import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Avatar, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  AccountBalanceWalletOutlined,
  KeyboardArrowDownOutlined,
} from "@mui/icons-material";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import ErrorIcon from "@mui/icons-material/Error";
import CommonDrawer from "../../drawer/CommonDrawer";
import Text from "../../text/Text";
import {
  amountIncludesText,
  apply,
  btc,
  confirmPayment,
  lnurl,
  memoRequiredMsg,
  networkFeeExceedMsg,
  paymentFailSubMessage,
  paymentFor,
  paymentFrom,
  payToTxt,
  payVia,
  payViaMsg,
  remove,
  sats,
  sendWithHighFee,
  totalAmount as totalAmountTxt,
  viaSpeedWallet,
  XAUT,
  notes,
  addNoteTxt,
} from "../../messages";
import { checkCircleCurved, couponCodeIconGrey, loaderImg } from "../../images";
import CustomDivider from "../../divider/Divider";
import Button from "../../button/Button";
import WithdrawPayViaModal from "./WithdrawPayViaModal";
import { CustomPopper } from "../../popper/Popper";
import {
  ethereumLabel,
  lightningLabel,
  onChainLabel,
  payRequest,
  showAmount,
  solanaLabel,
  stableCoins,
  tonLabel,
  tronLabel,
  withdrawRequest,
} from "../../constants";
import useExchangeAndSpeedFeeHook from "../../../hooks/useExchangeAndSpeedFeeHook";
import useEstimatedFees from "../../../hooks/useEstimatedFees";
import useBalanceAndPaymentMethods from "../../../hooks/useBalanceAndPaymentMethods";
import { useSession } from "../../../hooks/useSession";
import useCouponCodes from "../../../hooks/useCouponCodes";
import { CustomLink } from "../../link/Link";
import CommonSkeleton from "../../CommonSkeleton";
import useSendPayment from "../../../hooks/useSendPayment";
import EnterAmountMiniApp from "./EnterAmountMiniApp";
import useTransactionMinMaxAmount from "../../../hooks/useTransactionMinMaxAmount";
import useSendButtonCalculation from "../../../hooks/useSendButtonCalculation";
import PaymentFailModal from "../../PaymentFailModal";
import { Input } from "../../input/Input";

const dividerCss = {
  margin: "15px 0",
};

function WithdrawModal({
  isOpen,
  setIsOpen,
  setIsConfirmModalOpen,
  amount,
  setAmount,
  data,
  currency,
  appDetails,
  setCurrency,
  currencyExchangeRate,
  errorMsg,
  isShowErrorModal,
  setIsShowErrorModal,
}) {
  const [imgSrc, setImgSrc] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isPayViaOpen, setIsPayViaOpen] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [speedFee, setSpeedFee] = useState(0);
  const [fixedFee, setFixedFee] = useState(0);
  const [networkFeeAmount, setNetworkFeeAmount] = useState(0);
  const [calculatedSpeedFees, setCalculatedSpeedFees] = useState(0);
  const [balance, setBalance] = useState(null);
  const [balanceData, setBalanceData] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [exchangeRate, setExchangeRate] = useState(1);
  const [convertedAmount, setConvertedAmount] = useState(null);
  const [networkFeeLoader, setNetworkFeeLoader] = useState(true);
  const [conversionFeeAmount, setConversionFeeAmount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponCodes, setCouponCodes] = useState([]);
  const [suggestedCoupon, setSuggestedCoupon] = useState(null);
  const [withdrawDetails, setWithdrawDetails] = useState(null);
  const [withdrawStatus, setWithdrawStatus] = useState("");
  const [paymentFailedMsg, setPaymentFailedMsg] = useState(
    paymentFailSubMessage
  );
  const [isPaymentFailed, setIsPaymentFailed] = useState(false);
  const [isNodeRestricted, setIsNodeRestricted] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [isEnterAmountModal, setIsEnterAmountModal] = useState(false);
  const [minTransactionAmt, setMinTransactionAmt] = useState(0);
  const [maxTransactionAmt, setMaxTransactionAmt] = useState(0);
  const [sendMaxAmount, setSendMaxAmount] = useState(null);
  const [errorResponse, setErrorResponse] = useState(
    JSON.stringify({
      detail: "withdraw not working. Insufficient balance.",
    })
  );
  const [isRedemptionFail, setRedemptionFail] = useState(false);
  const [buttonColor, setButtonColor] = useState(null);
  const [memo, setMemo] = useState(null);

  const { session } = useSession();

  const { showBtcAmount, walletCurrency, payViaMethods } = useSelector(
    (state) => state.common
  );

  const { code: targetedCurrency } = {
    ...walletCurrency,
  };

  const { display_name, logo_images } = appDetails?.data || {};

  const queryParam = `limit=${10}`;
  const accountCountry = session?.account?.account?.country;
  const isLightningMethod = data?.withdraw_method === lightningLabel;
  const isLnUrlMethodSend =
    data?.withdraw_method === lnurl && data?.tag === payRequest;
  const isLnUrlMethodReceive =
    data?.withdraw_method === lnurl && data?.tag === withdrawRequest;

  const lightningWithLn =
    isLightningMethod || isLnUrlMethodSend || isLnUrlMethodReceive;
  const isOnchainMethod = data?.withdraw_method === onChainLabel;
  const isEthereumMethod = data?.withdraw_method === ethereumLabel;
  const isTronMethod = data?.withdraw_method === tronLabel;
  const isSolanaMethod = data?.withdraw_method === solanaLabel;
  const isTonMethod = data?.withdraw_method === tonLabel;
  const isLightningStableCoin =
    isLightningMethod && stableCoins.includes(currency);
  const accountId = session?.account?.account?.id;

  let isAmountLoader = buttonLoader && showLoader;

  const isPayViaAvailable = !isLnUrlMethodReceive;

  const showCouponCodes = !isLnUrlMethodReceive;

  //calculate exchange rate and speed fees
  useExchangeAndSpeedFeeHook({
    amount,
    currency,
    targetedCurrency,
    checkExchange: false,
    showBtcAmount,
    lightningWithLn,
    isOnchainMethod,
    isEthereumMethod,
    isTronMethod,
    isSolanaMethod,
    isLnUrlMethodReceive,
    setSpeedFee,
    setFixedFee,
    setButtonLoader,
    setExchangeRate,
    setConvertedAmount,
  });

  //get balance and payment methods array
  useBalanceAndPaymentMethods({
    currency,
    data,
    isLightningMethod,
    isLnUrlMethodSend,
    payViaMethods,
    setBalanceData,
    setBalance,
    setPaymentMethods,
  });

  //calculate estimate fees and speed fee calculation
  useEstimatedFees({
    amount,
    currency,
    data,
    speedFee,
    fixedFee,
    balance,
    showBtcAmount,
    isOnchainMethod,
    isLightningMethod,
    isLnUrlMethodSend,
    isLnUrlMethodReceive,
    isEthereumMethod,
    isTronMethod,
    isSolanaMethod,
    isLightningStableCoin,
    setCalculatedSpeedFees,
    setNetworkFeeLoader,
    setNetworkFeeAmount,
    setConversionFeeAmount,
    setButtonLoader,
    isFromMiniApp: true,
  });

  //get coupon codes
  const { validateCoupon } = useCouponCodes({
    amount,
    data,
    currency,
    session,
    queryParam,
    appliedCoupon,
    showCouponCodes,
    setCouponCodes,
    setAppliedCoupon,
    setButtonLoader,
    setNetworkFeeLoader,
  });

  //send payment API
  const { handleSubmit } = useSendPayment({
    amount,
    currency,
    note: memo,
    data,
    accountId,
    appliedCoupon,
    withdrawDetails,
    isLnUrlMethodReceive,
    showBtcAmount,
    setErrorResponse,
    withdrawStatus,
    setButtonLoader,
    setPaymentFailedMsg,
    setIsPaymentFailed,
    setRedemptionFail,
    setIsShowErrorModal,
    setWithdrawDetails,
    setWithdrawStatus,
    setIsNodeRestricted,
    setIsSendModalOpen: setIsOpen,
    setIsPaymentProcessing,
    setIsConfirmModalOpen,
    setIsOpen,
    isFromMiniApp: true,
    setShowLoader,
  });

  //set min max amount for lnurl
  useTransactionMinMaxAmount({
    data,
    currency,
    currencyExchangeRate,
    isOnchainMethod,
    isLnUrlMethodSend,
    isLnUrlMethodReceive,
    setMaxTransactionAmt,
    setMinTransactionAmt,
    setSendMaxAmount,
  });

  const { sendCalculations } = useSendButtonCalculation({
    amount,
    currency,
    balance,
    minTransactionAmt,
    maxTransactionAmt,
    sendMaxAmount,
    showBtcAmount,
    networkFeeAmount,
    calculatedSpeedFees,
    isLnUrlMethodReceive,
    isLnUrlMethodSend,
    isEthereumMethod,
    isTronMethod,
    isLightningMethod,
    isOnchainMethod,
    setButtonColor,
  });

  useEffect(() => {
    if (isLnUrlMethodReceive) {
      setTotalAmount(Number(amount) - Number(calculatedSpeedFees));
    } else {
      setTotalAmount(
        Number(networkFeeAmount) +
          Number(calculatedSpeedFees) +
          Number(amount) +
          Number(conversionFeeAmount)
      );
    }
  }, [networkFeeAmount, calculatedSpeedFees, amount, conversionFeeAmount]);

  useEffect(() => {
    if (logo_images?.original) {
      setImgSrc(logo_images.original);
    }
  }, [logo_images]);

  useEffect(() => {
    const activeCoupon = couponCodes?.find(
      (couponCode) => couponCode.is_eligible
    );
    setSuggestedCoupon(activeCoupon);
  }, [couponCodes]);

  useEffect(() => {
    if (data) {
      const isZeroAmount = data.amount === 0 && currency === sats;
      const isLnURLDifferentAmount =
        isLnUrlMethodSend && data.min_sendable !== data.max_sendable;
      const isOnchainWithZeroAmount =
        isOnchainMethod && (!amount || amount === 0);
      const isOpenEnterAmount =
        isEthereumMethod || isTronMethod || isSolanaMethod || isTonMethod;

      if (
        isZeroAmount ||
        isLnURLDifferentAmount ||
        isOnchainWithZeroAmount ||
        isOpenEnterAmount
      ) {
        setIsEnterAmountModal(true);
      }
    }
  }, [data]);

  const handleDrawerClose = () => {
    setIsOpen(false);
  };

  const handleAnchor = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleCloseAnchor = (e, index) => {
    setAnchorEl(null);
  };

  const renderPayViaSection = () => {
    return (
      <Box display="flex" justifyContent="space-between">
        <Text
          className="default-text"
          size={14}
          font="semibold"
          sx={{ display: "flex", alignItems: "center" }}
        >
          {isPayViaAvailable ? payVia : payToTxt}
          {isPayViaAvailable && (
            <>
              <ErrorIcon
                aria-describedby="pay-via"
                className="error-icon"
                onClick={(e) => handleAnchor(e)}
              />
              <CustomPopper
                id="pay-via"
                disablePortal={true}
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                position="top"
                handleClose={(e) => handleCloseAnchor(e)}
                sx={{ width: "253px" }}
              >
                <Text variant="inherit" font="regular" size={12}>
                  {payViaMsg}
                </Text>
              </CustomPopper>
            </>
          )}
        </Text>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            cursor: "pointer",
          }}
          onClick={() => {
            if (paymentMethods.length > 1 && isPayViaAvailable)
              setIsPayViaOpen(true);
          }}
        >
          <AccountBalanceWalletOutlined
            sx={{
              color: "#848B9E",
            }}
          />
          <Text font="semibold" size={16}>
            {currency === sats ? btc : currency}
          </Text>

          {paymentMethods.length > 1 && isPayViaAvailable && (
            <KeyboardArrowDownOutlined
              sx={{
                color: "#848B9E",
              }}
            />
          )}
        </Box>
      </Box>
    );
  };

  const renderMemoSection = () => {
    return (
      <Box display="flex" flexDirection="column">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Text className="default-text" size={14} font="semibold">
            {notes}
          </Text>
          <Box display="flex" alignItems="center" flex={1}>
            <Input
              fullWidth
              showLabel={false}
              value={memo}
              onChange={(e) => {
                setMemo(e.target.value);
              }}
              type="text"
              customClass="ton-memo"
              placeholder={addNoteTxt}
            />
            <ModeEditOutlineOutlinedIcon
              sx={{
                color: "#848B9E",
              }}
            />
          </Box>
        </Box>
        <Box>
          <Text className="grey-text" size={12} font="regular">
            {memoRequiredMsg}
          </Text>
        </Box>
      </Box>
    );
  };

  const payViaModalProps = {
    amount,
    setAmount,
    currency,
    setCurrency,
    isOpen: isPayViaOpen,
    setIsOpen: setIsPayViaOpen,
    paymentMethods,
    setButtonLoader,
    setBalance,
    balanceData,
    data,
    isPayViaAvailable,
    minTransactionAmt,
    setMinTransactionAmt,
    maxTransactionAmt,
    setMaxTransactionAmt,
  };

  const handleApplyClick = async () => {
    if (appliedCoupon) {
      setAppliedCoupon(null);
    } else {
      const isValid = await validateCoupon(suggestedCoupon);
      if (isValid) {
        setAppliedCoupon(suggestedCoupon);
      }
    }
  };

  const enterAmountProps = {
    isOpen: isEnterAmountModal,
    setIsOpen: setIsEnterAmountModal,
    currencyFromDecode: currency,
    amount,
    setAmount,
    data,
    balance,
    networkFeeAmount,
    calculatedSpeedFees,
    isLnUrlMethodReceive,
    isLnUrlMethodSend,
    isEthereumMethod,
    isTronMethod,
    isLightningMethod,
    isOnchainMethod,
    minTransactionAmt,
    maxTransactionAmt,
    sendMaxAmount,
    setWithdrawModal: setIsOpen,
    exchangeRate,
  };

  const errorMessageText = () => {
    if (isPaymentFailed) {
      return (
        <Text
          className="error-text"
          font="regular"
          size={14}
          sx={{ marginTop: "15px" }}
        >
          {paymentFailedMsg}
        </Text>
      );
    }
    if (isShowErrorModal) {
      return (
        <Text
          className="error-text"
          font="regular"
          size={14}
          sx={{ marginTop: "15px" }}
        >
          {errorMsg}
        </Text>
      );
    }
  };

  const handlePaymentFailModalClose = () => {
    setIsShowErrorModal(false);
    setIsPaymentFailed(false);
    setIsPaymentProcessing(false);
    setRedemptionFail(false);
    setWithdrawDetails(null);
    setWithdrawStatus("");
  };

  const paymentFailProps = {
    isPaymentFailed,
    isNodeRestricted,
    isPaymentProcessing,
    isRedemptionFail,
    paymentFailedMsg,
    setIsSendModalOpen: setIsOpen,
    isShowErrorModal,
    setIsShowErrorModal,
    errorResponse,
    handleModalClose: handlePaymentFailModalClose,
  };

  return isEnterAmountModal && errorMsg === "" ? (
    <EnterAmountMiniApp {...enterAmountProps} />
  ) : (
    <CommonDrawer
      anchor="bottom"
      open={isOpen}
      onClose={handleDrawerClose}
      className="withdraw-drawer"
    >
      {(isPaymentFailed || isPaymentProcessing || isRedemptionFail) && (
        <PaymentFailModal {...paymentFailProps} />
      )}
      <Box padding="24px 16px">
        <Box className="withdraw-header flex-between">
          <Text className="default-text" size={20} font="bold" mb="20px">
            {confirmPayment}
          </Text>
          <CloseIcon className="close-icon" onClick={handleDrawerClose} />
        </Box>
        <Box className="payment-box">
          <Box className="app-details">
            <Box display="flex" alignItems="center" gap="10px">
              <Avatar
                src={imgSrc || loaderImg}
                alt="app-logo"
                className="list-avatar"
                variant="rounded"
                onError={() => {
                  setImgSrc(loaderImg);
                }}
              />
              <Box display="flex" flexDirection="column">
                <Text className="default-text" font="semibold" size={14}>
                  {isPayViaAvailable ? paymentFor : paymentFrom} {display_name}
                </Text>
                <Text className="grey-text" font="semibold" size={12}>
                  {isAmountLoader ? (
                    <CommonSkeleton variant="text" />
                  ) : (
                    showAmount({
                      amount,
                      currency:
                        showBtcAmount && currency === sats ? btc : currency,
                      showCommaSeparated: true,
                      appendCurrency: true,
                      multiplier:
                        stableCoins.includes(currency) || currency === XAUT,
                    })
                  )}
                </Text>
                <Text
                  className="grey-text"
                  font="regular"
                  size={12}
                  variant="subtitle"
                >
                  {viaSpeedWallet}
                </Text>
              </Box>
            </Box>
            <CustomDivider sx={dividerCss} />
            <Box display="flex" justifyContent="space-between">
              <Box display="flex" flexDirection="column">
                <Text className="default-text" size={14} font="semibold">
                  {totalAmountTxt}
                </Text>
                <Text className="grey-text" size={12} font="regular">
                  {amountIncludesText}
                </Text>
                {sendCalculations?.btnLabel === sendWithHighFee && (
                  <Text size={12} font="regular" sx={{ color: "#D88A2C" }}>
                    {networkFeeExceedMsg}
                  </Text>
                )}
              </Box>
              <Text className="default-text" size={14} font="semibold">
                {isAmountLoader ? (
                  <CommonSkeleton variant="text" width="20px" />
                ) : (
                  <>
                    ~{" "}
                    {showAmount({
                      amount: totalAmount,
                      currency:
                        showBtcAmount && currency === sats ? btc : currency,
                      showCommaSeparated: true,
                      appendCurrency: true,
                      multiplier:
                        stableCoins.includes(currency) || currency === XAUT,
                    })}
                  </>
                )}
              </Text>
            </Box>
            <CustomDivider sx={dividerCss} />
            {renderPayViaSection()}

            {isTonMethod && (
              <>
                <CustomDivider sx={dividerCss} />
                {renderMemoSection()}
              </>
            )}
          </Box>

          {suggestedCoupon && (
            <Box className="cashback-detail">
              <Box display="flex" alignItems="flex-start" gap={1}>
                {networkFeeLoader ? (
                  <CommonSkeleton width="25px" />
                ) : (
                  <img
                    src={appliedCoupon ? checkCircleCurved : couponCodeIconGrey}
                    height={20}
                    width={20}
                  />
                )}
                <Box display="flex" flexDirection="column" gap={0.5}>
                  {networkFeeLoader ? (
                    <CommonSkeleton variant="text" />
                  ) : (
                    <Text className="default-text" size={14} font="medium">
                      {appliedCoupon
                        ? appliedCoupon?.help_text
                        : suggestedCoupon?.help_text}
                    </Text>
                  )}
                </Box>
              </Box>
              <CustomLink
                color={appliedCoupon ? "error" : "primary"}
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  margin: "4px",
                }}
                onClick={handleApplyClick}
              >
                {networkFeeLoader ? (
                  <CommonSkeleton />
                ) : appliedCoupon ? (
                  remove.toUpperCase()
                ) : (
                  apply.toUpperCase()
                )}
              </CustomLink>
            </Box>
          )}

          {isPaymentFailed || isShowErrorModal ? (
            errorMessageText()
          ) : (
            <Button
              fullWidth
              variant="contained"
              label={sendCalculations?.btnLabel}
              loading={buttonLoader || networkFeeLoader}
              className="txt-uppercase"
              onClick={() => {
                handleSubmit();
              }}
              disabled={sendCalculations?.btnDisable}
              sx={{
                mt: "28px",
                ...(sendCalculations?.btnLabel === sendWithHighFee &&
                  !networkFeeLoader && {
                    backgroundColor: `${buttonColor} !important`,
                  }),
              }}
            />
          )}
        </Box>
      </Box>
      <WithdrawPayViaModal {...payViaModalProps} />
    </CommonDrawer>
  );
}

export default WithdrawModal;
